/**
 * «Один блок»: a map that is one block and the sky.
 *
 * "Гравці зявляються на одному блоці а навколо порожнеча... Ціль: побудувати
 * світ з одного такого блоку."
 *
 * Everything the map is lives here, and all of it is arithmetic: where the
 * block stands, and what falls out of it on the nth blow. Pure on purpose -
 * the same roll has to come out for the page and for the server, or two
 * people standing at the same block would take different things out of it.
 *
 * What it deliberately does **not** do is take the block away. Four blows
 * shake something loose and the шопопало-блок stays exactly where it was:
 * it is the floor, the spawn and the whole of the map's supply. Break it
 * once and the map would be over, with everybody falling.
 */

import { palette } from "../block/palette.js";
import {
  itemLabel, itemDef, itemTool, itemUse, PlainItemId, FIRST_NON_BLOCK_ITEM, type ItemId,
} from "../block/items.js";
import { spawnForItem } from "../entity/spawn.js";
import { isBlueprint, BLUEPRINT_BASE, BlockId, blockDef } from "../block/registry.js";

/**
 * The things that fall out on a lane of their own, and how often.
 *
 * Everything else shares one table and comes out as often as everything
 * else. These are kept off it and rolled first, so many times in a hundred
 * - because for these "as often as everything else" is wrong in both
 * directions. A lane with several things on it hands out one of them,
 * chosen by the same roll.
 *
 * A drafting stone is not a thing on the shelf, it is a building. The хата
 * is a house in one block; on a map that is one block and the sky it is the
 * event of the evening, and three in a hundred is rare enough to be one and
 * common enough to be seen. The кремль's is a fortress sixty-one blocks on
 * a side, which on this map is the map over: nowhere to stand it, nothing
 * left to build once it has stood. It is not on this list and not on the
 * table, and so never falls out at all.
 *
 * A танк is a танк. Five in a thousand: it was five in a hundred, which
 * was asked for and then found to be far too many - "дуже часто випадають
 * танки". Rare enough that one is an event.
 *
 * And the digging and fishing tools, all of them together, ten in a
 * hundred: "зменш до 10% шанс отримати вудку сапку лопату та кайло". The
 * rod, the hoe, and every shovel and pickaxe there is - not the axe and
 * not the sabre, which were not named and stay on the table.
 *
 * The chances add up down the list, and the list must stay under a
 * thousand. Per thousand rather than per hundred so that a танк can be
 * rarer than one in a hundred without the others turning into fractions.
 */
interface Lane {
  readonly items: readonly ItemId[];
  readonly inAThousand: number;
}

/**
 * Every rod, hoe, shovel and pickaxe, off the palette and in id order.
 *
 * Asked of the items rather than written out, so a new tier of pickaxe
 * lands on this lane the day it is added rather than the day somebody
 * remembers it. Sorted, because a lane hands out by position and the
 * palette's own order is by shelf.
 */
function diggingTools(): readonly ItemId[] {
  return palette()
    .map((entry) => entry.id)
    .filter((id) => {
      const tool = itemTool(id);
      const use = itemUse(id)?.kind;
      return tool === "pickaxe" || tool === "shovel" ||
        use === "till" || use === "fish";
    })
    .sort((a, b) => a - b);
}

/*
 * Built once and kept.
 *
 * The palette never changes while the game runs, and building it is most
 * of a hundredth of a second. Asking for it afresh inside `hasLane`, for
 * every entry the table filters, made one roll of the block cost forty
 * milliseconds - and the node suite rolls it thirty thousand times, which
 * was nineteen minutes of a check that used to take one. The game itself
 * only noticed as a stutter on the fourth blow; the suite noticed as a
 * deploy that never finished.
 */
let cachedLanes: readonly Lane[] | null = null;
let cachedLaneItems: ReadonlySet<ItemId> | null = null;
let cachedTable: readonly ItemId[] | null = null;

function lanes(): readonly Lane[] {
  cachedLanes ??= [
    { items: [BLUEPRINT_BASE], inAThousand: 30 },
    { items: [PlainItemId.Tank], inAThousand: 5 },
    { items: diggingTools(), inAThousand: 100 },
  ];
  return cachedLanes;
}

/** Whether it has a lane of its own, and so is not on the common table. */
function hasLane(id: ItemId): boolean {
  cachedLaneItems ??= new Set(lanes().flatMap((lane) => lane.items));
  return cachedLaneItems.has(id);
}

/**
 * What the ground is made of, as opposed to what is dug out of it.
 *
 * "Нехай всі блоки земель та каміння випадають по 10 штук одразу." Soil and
 * stone, and not the ores: руда is its own word, and ten iron a blow would
 * make this the richest mine in the game.
 */
const EARTH_AND_STONE: readonly ItemId[] = [
  BlockId.Grass, BlockId.Dirt, BlockId.Chornozem, BlockId.Sand, BlockId.Clay,
  BlockId.Peat, BlockId.Stone, BlockId.Granite,
];

/**
 * How high in the world the block hangs.
 *
 * Well clear of the floor of the world: what is under it is meant to read as
 * nothing at all, and a block you can see the bottom of the world from is a
 * block standing in a pit.
 */
export const ONE_FLOOR = 72;

/** Where the block is, in a world this many blocks across. */
export function oneBlockAt(span: number): { x: number; y: number; z: number } {
  const middle = Math.floor(span / 2);
  return { x: middle, y: ONE_FLOOR, z: middle };
}

/**
 * What the table refuses outright.
 *
 * Листя - "прибери листя з дропу": a block that comes with every tree and
 * builds nothing is a slot on the belt thrown away. And the вибухоблок -
 * "прибери вибухоблок з дропу": on a map that is one block and whatever you
 * have bridged out of it, a block that takes its neighbours with it is not a
 * gift, it is the map ending in your hand.
 */
function refused(id: ItemId): boolean {
  if (id === BlockId.Boom) return true;
  // And the оркське лігво - "прибери лігво орків з дропу": a block that
  // breeds orcs on a block one player wide is a siege on a stool.
  if (id === BlockId.OrcSpawner) return true;
  return id < FIRST_NON_BLOCK_ITEM && blockDef(id).name.endsWith("_leaves");
}

/**
 * Everything that can fall out in the ordinary way, in a fixed order.
 *
 * The palette, which is every holdable thing in the game: blocks, tools,
 * food, and the creatures - "будь-який з існуючих блоків чи речей чи істот".
 * A creature comes out as the thing you let it out of, which is how this
 * game already carries a свиня about, and means a рій of orcs cannot appear
 * in your face the moment you touch the block.
 *
 * Minus the креслярські камені, all of them: the кремль's never falls out
 * and the хата's has a lane of its own - see `RARE_STONE`.
 *
 * Sorted by id so the order is the same everywhere and for ever: a drop
 * table that depends on the order a module happened to be built in is a
 * drop table that changes when somebody adds a block.
 */
export function oneBlockTable(): readonly ItemId[] {
  cachedTable ??= palette()
    .map((entry) => entry.id)
    .filter((id) => !isBlueprint(id) && !hasLane(id) && !refused(id))
    .sort((a, b) => a - b);
  return cachedTable;
}

/**
 * What the nth blow shakes loose.
 *
 * Hashed from the world's seed and which break this is, so it is the same
 * for everyone at that block and cannot be re-rolled by leaving and coming
 * back. Not from the block's position: there is only ever one of them.
 */
export function oneBlockDrop(seed: number, breakNumber: number): ItemId {
  const table = oneBlockTable();
  /*
   * Mixed properly, in two rounds.
   *
   * One round of this was not enough: across forty blows it handed out only
   * twenty-four different things, where drawing at random from a hundred and
   * sixty-four should give about thirty-five. Neighbouring break numbers came
   * out close to each other and landed in the same cell of the table, which
   * on this map reads as "the block keeps giving me dirt".
   */
  let h = Math.imul(seed ^ 0x9e3779b9, 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (breakNumber + 0x165667b1), 0xc2b2ae35) >>> 0;
  h = ((h ^ (h >>> 15)) >>> 0);
  h = Math.imul(h, 0x27d4eb2f) >>> 0;
  h = ((h ^ (h >>> 13)) >>> 0);
  h = Math.imul(h, 0x85ebca6b) >>> 0;
  /*
   * Unsigned, and that is not a detail.
   *
   * `h ^= h >>> 16` on its own gives a *signed* thirty-two bit number, so
   * half the rolls came out negative, `table[negative]` is undefined, and
   * every one of them fell through to the first row of the table. On this
   * map that reads as "the block keeps giving me the same thing": fourteen
   * different things in forty blows instead of thirty-five.
   */
  h = ((h ^ (h >>> 16)) >>> 0);
  /*
   * The lanes first, off the low digits; the table off the rest.
   *
   * Two draws from one hash, and they must not be the same digits: a lane
   * decided by `h % 100` and a row decided by `h % table.length` would be
   * correlated, and "which things come the blow after a танк" is not a
   * question anybody should be able to answer.
   */
  let roll = h % 1000;
  const row = Math.floor(h / 1000);
  for (const lane of lanes()) {
    if (roll < lane.inAThousand) {
      return lane.items[row % lane.items.length] ?? table[0]!;
    }
    roll -= lane.inAThousand;
  }
  return table[row % table.length] ?? table[0]!;
}

/**
 * Whether a creature shaken out of the block walks out of it.
 *
 * Most do: a свиня, a тур, an orc stand up on the block and are there for
 * everybody at once - "хочу щоб істоти вискакували одразу". Two kinds do
 * not. A карась in the open air would drown in it, and the villagers are
 * not the server's to walk about yet; both come out as the thing you let
 * them out of, the same as a tool or a block would.
 *
 * Written here rather than in the realm's `switch`, because the check that
 * counts what was handed over has to walk the same rule as the world that
 * handed it - and a check with its own idea of which creatures walk agreed
 * with itself right up until the roll sequence changed under it.
 */
export function oneBlockWalksOut(item: ItemId): boolean {
  const def = spawnForItem(item);
  if (def === undefined) return false;
  return def.kind !== "karas" && def.kind !== "selianyn" &&
    def.kind !== "selianka";
}

/** How many of it: one of most things, a handful of the plain ones. */
export function oneBlockCount(item: ItemId): number {
  // A creature is one creature, and a drafting stone is one building.
  if (spawnForItem(item) !== undefined || isBlueprint(item)) return 1;
  /*
   * The ground by the armful, everything else by the handful.
   *
   * A world is built out of earth and stone before it is built out of
   * anything else, and ten a blow is what lets the block be a quarry rather
   * than a queue at a counter. Capped at what a stack of the thing can hold
   * - three танки was three танки in a slot that holds one.
   */
  const many = EARTH_AND_STONE.includes(item) ? 10 : 3;
  return Math.min(many, itemDef(item).maxStack ?? many);
}

/**
 * Where a creature shaken out of the block lands.
 *
 * On top of it and a step to the side, so it does not arrive inside whoever
 * is standing there - and a step is all there is: everything else is sky, so
 * whatever comes out has one cell to be born on and then falls, which is
 * part of the map rather than a fault in it.
 */
export function oneBlockBirth(
  at: { x: number; y: number; z: number },
): { x: number; y: number; z: number } {
  return { x: at.x + 0.5, y: at.y + 1.1, z: at.z + 0.5 };
}

/** What to tell the player they just got, in their own language. */
export function oneBlockSaid(item: ItemId, lang: "uk" | "en"): string {
  return itemLabel(item, lang);
}
